import { useAtividades } from 'hooks/useAtividades';
import FaturamentoModal from 'pages/agenda/FaturamentoModal';
import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Dropdown, OverlayTrigger, Popover, Spinner, Table } from 'react-bootstrap';
import { FaArrowLeft, FaCommentAlt } from 'react-icons/fa'; // Ícone para indicação de observação
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  atualizarStatusLaudo,
  buscarAtividades,
  excluirFaturamento,
  mensagemFaturamento,
} from 'services/atividades';
import history from 'services/history';
import {
  getAtividadeRoute,
  isConsultaOuRetornoAtividade,
  isConsultaOuRetornoProcedi,
  laudos,
} from 'utils/helpers';
import { buildPacientesSearchFromAtividadesQuery } from 'utils/pacientesSearch';
import ErroBuscaAtividades from './ErroBuscaAtividades';

function podeLaudar(atividade) {
  const codProcedi = Number(atividade?.CODPROCEDI);
  if (isConsultaOuRetornoProcedi(codProcedi)) return true;
  if (codProcedi === laudos.tilt) return true;
  if (laudos.ecg.includes(codProcedi)) return true;
  return Boolean(
    getAtividadeRoute({ action: 'laudar', codProcedi, codLaudo: atividade?.CODLAUDO })
  );
}

function podeVisualizar(atividade) {
  const codProcedi = Number(atividade?.CODPROCEDI);
  if (laudos.ecg.includes(codProcedi)) return true;
  return Boolean(
    getAtividadeRoute({ action: 'visualizar', codProcedi, codLaudo: atividade?.CODLAUDO })
  );
}

// Componente para renderizar o Popover de Observação/Auditoria
function ObservacaoPopover({ observacao, procedimento }) {
  if (!observacao || observacao.trim() === '') return null;

  // Formata os logs colchetes em quebras de linha limpas
  const linhasLog = observacao.split(' [').map((item, index) => (index > 0 ? `[${item}` : item));

  const popoverContent = (
    <Popover id="popover-observacao" style={{ maxWidth: '350px' }}>
      <Popover.Header as="h3" className="fs-6 fw-bold bg-light">
        Histórico / Obs: {procedimento}
      </Popover.Header>
      <Popover.Body className="small text-secondary" style={{ whiteSpace: 'pre-line' }}>
        {linhasLog.map((linha, idx) => (
          <div key={idx} className={idx > 0 ? 'mt-1 pt-1 border-top' : ''}>
            {linha}
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={popoverContent}>
      <span className="ms-2 text-info style-pointer cursor-pointer" title="Ver Observações">
        <FaCommentAlt size={13} />
      </span>
    </OverlayTrigger>
  );
}

export default function FormAtividades() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const codpac = query.get('codpac');
  const nomePaciente =
    query.get('nomePaciente') || location.state?.nomePaciente || location.state?.nome || '';
  const somenteEcg = query.get('ecg') === '1' || location.state?.ecg === true;
  const codFunc = query.get('codFunc');
  const nomeFunc = query.get('nomeFunc');
  const [atividades, setAtividades] = useState([]);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { handleEcg, handleTilt, handleProntuario } = useAtividades();

  // Estados para o FaturamentoModal
  const [showModalFaturamento, setShowModalFaturamento] = useState(false);
  const [faturamentoSelecionado, setFaturamentoSelecionado] = useState(null);

  const fetchAtividades = useCallback(async () => {
    if (!codpac) return;

    setIsLocalLoading(true);
    setFetchError(null);
    try {
      const data = await buscarAtividades(codpac);
      const lista = somenteEcg
        ? data.filter((a) => laudos.ecg.includes(Number(a.CODPROCEDI)))
        : data;
      setAtividades(lista);
    } catch (error) {
      setFetchError(error);
    } finally {
      setIsLocalLoading(false);
    }
  }, [codpac, somenteEcg]);

  useEffect(() => {
    fetchAtividades();
  }, [fetchAtividades]);

  function handleVoltarCadastroPaciente() {
    const pacientesSearch = buildPacientesSearchFromAtividadesQuery({
      nomePaciente,
      codpac,
    });
    history.push('/pacientes', { pacientesSearch });
  }

  async function handleExcluirFaturamento(faturamento) {
    const confirmacao = window.confirm(
      `Deseja realmente excluir o faturamento (Fatura nº ${faturamento.CODFATURA}) do procedimento ${faturamento.DESCPROCEDI}?`
    );

    if (!confirmacao) return;

    try {
      await excluirFaturamento(faturamento.CODFATURA);
      setAtividades((prev) => prev.filter((item) => item.CODFATURA !== faturamento.CODFATURA));
      toast.success('Faturamento excluído com sucesso.');
    } catch (error) {
      toast.error(mensagemFaturamento(error, 'Erro ao excluir faturamento.'));
    }
  }

  async function handleAtualizarStatus(faturamento, payload, acaoNome) {
    try {
      await atualizarStatusLaudo(faturamento?.CODFATURA, payload);
      toast.success(`Laudo ${acaoNome} com sucesso.`);
      fetchAtividades(); // Recarrega para atualizar os campos GUIA1, ENTREGUE e OBSERVACAO
    } catch (error) {
      toast.error(mensagemFaturamento(error, `Erro ao ${acaoNome.toLowerCase()} laudo.`));
    }
  }

  function handleAbrirEdicaoFaturamento(faturamento) {
    const dadosFaturamento = {
      ...faturamento,
      ID_FATURAMENTO: faturamento.CODFATURA,
      FATURADO: true,
      CODPAC: faturamento.CODPAC,
      NOME: faturamento.NOMEPAC || nomePaciente,
      NOMEFUNC: faturamento.NOMEFUNC,
      CODMED_CONVENIADO: String(faturamento.CODMEDICO || ''),
      NOMEMED_CONVENIADO: faturamento.NOMEMED_CONVENIADO || faturamento.NOMEMED || '',
      CODMED_SOLICITOU: String(faturamento.CODENCAMINHOU || ''),
      NOMEMED_SOLICITOU: faturamento.NOMEMED_SOLICITOU || faturamento.NOMEMED || '',
      CODMED_REALIZOU: String(faturamento.CODREALIZOU || ''),
      NOMEMED_REALIZOU: faturamento.NOMEMED_REALIZOU || faturamento.CODINOME || '',
      CODCONV: String(faturamento.CODCONV || ''),
      CODPROCEDI: String(faturamento.CODPROCEDI || ''),
      GUIA: faturamento.GUIA ? String(faturamento.GUIA) : '',
      VALOR: faturamento.VLRLAUDO ? String(faturamento.VLRLAUDO) : '',
      FATURAR_ECG: Boolean(faturamento.FATURAR_ECG),
      CODCONV_ECG: faturamento.CODCONV_ECG ? String(faturamento.CODCONV_ECG) : '',
      GUIA_ECG: faturamento.GUIA_ECG ? String(faturamento.GUIA_ECG) : '',
      VALOR_ECG: faturamento.VALOR_ECG ? String(faturamento.VALOR_ECG) : '',
      DATAAGENDA: faturamento.dataFormatada || faturamento.DATALAUDO,
      OBSERVACAO: faturamento.OBSERVACAO || '',
    };

    setFaturamentoSelecionado(dadosFaturamento);
    setShowModalFaturamento(true);
  }

  function handleDropdownAction(action, a) {
    const codProcedi = Number(a?.CODPROCEDI);
    const route = getAtividadeRoute({
      action,
      codProcedi,
      codLaudo: a?.CODLAUDO,
    });

    switch (action) {
      case 'laudar':
        if (isConsultaOuRetornoProcedi(codProcedi)) {
          handleProntuario(a);
          break;
        }
        if (codProcedi === laudos.tilt) {
          handleTilt(a);
          break;
        }
        if (laudos.ecg.includes(Number(codProcedi))) {
          handleEcg(a);
          break;
        }
        if (route) {
          history.push(route);
          break;
        }
        toast.warning('Procedimento não liberado na web.');
        break;

      case 'visualizar':
        if (laudos.ecg.includes(Number(codProcedi))) {
          handleEcg(a);
          break;
        }
        if (route) {
          history.push(route);
          break;
        }
        toast.warning('Visualização indisponível para este procedimento.');
        break;

      case 'editar':
        handleAbrirEdicaoFaturamento(a);
        break;

      case 'excluir':
        handleExcluirFaturamento(a);
        break;

      case 'liberar':
        handleAtualizarStatus(a, { guia1: 'SIM' }, 'liberado');
        break;

      case 'entregar':
        handleAtualizarStatus(a, { entregue: 'SIM' }, 'marcado como entregue');
        break;

      default:
        toast.warning('Ação não disponível.');
    }
  }

  return (
    <>
      <Card>
        <Card.Header className="mt-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
          <Card.Title className="h5 mb-0">
            Atividades de {nomePaciente || atividades[0]?.NOMEPAC || ''} - #
            {atividades[0]?.CODPAC || codpac || ''}
          </Card.Title>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            className="rounded-pill"
            onClick={handleVoltarCadastroPaciente}
          >
            <FaArrowLeft className="me-1 text-primary" aria-hidden="true" />
            Voltar ao cadastro
          </Button>
        </Card.Header>
        <Card.Body>
          {isLocalLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : fetchError && atividades.length === 0 ? (
            <ErroBuscaAtividades error={fetchError} onRetry={fetchAtividades} />
          ) : atividades.length === 0 ? (
            <p className="text-center text-muted py-5 mb-0">
              {somenteEcg
                ? 'Nenhum ECG encontrado para este paciente.'
                : 'Nenhuma atividade encontrada para este paciente.'}
            </p>
          ) : (
            <>
              {fetchError ? (
                <div className="mb-3">
                  <ErroBuscaAtividades error={fetchError} onRetry={fetchAtividades} stale />
                </div>
              ) : null}
              <Table responsive striped bordered hover size="sm" className="mb-0 align-middle">
                <thead className="table-primary text-center">
                  <tr>
                    <th>Procedimento</th>
                    <th>Convênio</th>
                    <th>Data</th>
                    <th>Liberado</th>
                    <th>Entregue</th>
                    <th>Laudo</th>
                    <th>Rlz</th>
                    <th>Méd</th>
                    <th>Enc</th>
                    <th>Vlr</th>
                    <th>Guia</th>
                    <th>Dt Fatura</th>
                    <th>Func</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {atividades.map((a) => {
                    const exibirLaudar = podeLaudar(a);
                    const exibirVisualizar = podeVisualizar(a);
                    const descProcedimento = a.DESCPROCEDI?.split(' ').slice(0, 2).join(' ');

                    return (
                      <tr key={a.CODFATURA}>
                        <td>
                          <div className="d-flex align-items-center justify-content-between">
                            <span>{descProcedimento}</span>
                            {/* Ícone com Popover do histórico/observação */}
                            <ObservacaoPopover
                              observacao={a.OBSERVACAO}
                              procedimento={descProcedimento}
                            />
                          </div>
                        </td>
                        <td className="text-center">{a.SIGLACONV}</td>
                        <td className="text-center">{a.dataFormatada}</td>
                        <td className="fw-bold text-primary text-center">{a.GUIA1}</td>
                        <td className="fw-bold text-success text-center">{a.ENTREGUE}</td>
                        <td className="text-center">{a.CODLAUDO}</td>
                        <td className="text-center">{a.CODINOME}</td>
                        <td className="text-center">{a.CODMEDICO}</td>
                        <td className="text-center">{a.CODENCAMINHOU}</td>
                        <td className="text-end px-2">{a.VLRLAUDO}</td>
                        <td className="text-center">{a.GUIA}</td>
                        <td className="text-center">{a.dataFatura}</td>
                        <td className="text-success">{a.NOMEFUNC}</td>
                        <td className="text-center">
                          <Dropdown align="end">
                            <Dropdown.Toggle
                              variant="outline-primary"
                              size="sm"
                              className="rounded-pill px-3"
                              id={`dropdownMenuButton-${a.CODFATURA}`}
                            >
                              Ações
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              popperConfig={{
                                strategy: 'fixed',
                                modifiers: [
                                  {
                                    name: 'preventOverflow',
                                    options: { boundary: 'viewport' },
                                  },
                                ],
                              }}
                            >
                              {exibirLaudar ? (
                                <Dropdown.Item
                                  as="button"
                                  onClick={() => handleDropdownAction('laudar', a)}
                                >
                                  {isConsultaOuRetornoAtividade(a) ? 'Prontuário' : 'Laudar'}
                                </Dropdown.Item>
                              ) : null}
                              {exibirVisualizar ? (
                                <Dropdown.Item
                                  as="button"
                                  onClick={() => handleDropdownAction('visualizar', a)}
                                >
                                  Visualizar
                                </Dropdown.Item>
                              ) : null}

                              {exibirLaudar || exibirVisualizar ? <Dropdown.Divider /> : null}

                              <Dropdown.Item
                                as="button"
                                onClick={() => handleDropdownAction('editar', a)}
                              >
                                Editar Faturamento
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                className="text-danger"
                                onClick={() => handleDropdownAction('excluir', a)}
                              >
                                Excluir Faturamento
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                as="button"
                                className="text-primary"
                                onClick={() => handleDropdownAction('liberar', a)}
                              >
                                Liberar Laudo
                              </Dropdown.Item>
                              <Dropdown.Item
                                as="button"
                                className="text-success"
                                onClick={() => handleDropdownAction('entregar', a)}
                              >
                                Laudo Entregue
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
        </Card.Body>
      </Card>

      <FaturamentoModal
        show={showModalFaturamento}
        onHide={() => {
          setShowModalFaturamento(false);
          setFaturamentoSelecionado(null);
        }}
        agenda={faturamentoSelecionado}
        codFunc={codFunc}
        nomeFunc={nomeFunc}
        onFaturado={fetchAtividades}
      />
    </>
  );
}
